import React, { useMemo, useEffect, useCallback } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useFormContext, Controller } from "react-hook-form";
import KaboodleModal from "./KaboodleModal";
import Button from "components/Button/Button";
import { TextInput } from "components/inputs";
import { useCreatePolicyMutation } from "services/policy-api";
import { hasApiError } from "utils/error-utils";
import Card from "components/Card/Card";
import { useParams, useNavigate } from "react-router-dom";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  policy_number: string;
  name: string;
};

const CreatePolicyModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { rreId } = useParams<{ rreId?: string }>();

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useFormContext<FormData>();

  const [createPolicy, { error, isSuccess }] = useCreatePolicyMutation();

  useEffect(() => {
    if (hasApiError(error)) {
      Object.entries(error.data).forEach(([field, messages]) => {
        setError(field as keyof FormData, {
          type: "manual",
          message: Array.isArray(messages) ? messages[0] : messages,
        });
      });
    }
  }, [error, setError]);

  useEffect(() => {
    if (isOpen) {
      reset({
        policy_number: "",
        name: "",
      });
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [onClose, isSuccess]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data: FormData) => {
      try {
        const result = await createPolicy({
          data: {
            ...data,
            rre: rreId,
          },
        }).unwrap();

        if ((result as { id: number }).id) {
          reset();
          onClose();
          navigate(
            `/section-111/rres/${rreId}/policies/${(result as { id: number }).id}/overview`,
          );
        }
      } catch (err) {
        console.error("Failed to create Policy:", err);
      }
    },
    [createPolicy, rreId, reset, onClose, history],
  );

  const handleClose = useCallback(() => {
    reset({
      policy_number: "",
      name: "",
    });
    onClose();
  }, [reset, onClose]);

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [handleSubmit, onSubmit]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Create Policy">
        <Controller
          control={control}
          name="name"
          rules={{ required: "Policy Name is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Name"
              label="Policy Name"
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="policy_number"
          rules={{ required: "Policy Number is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Policy Number"
              label="Policy Number"
              errors={errors}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CreatePolicyModal;
